import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed or dismissed
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (dismissed || isStandalone) {
      return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // For iOS, show banner after a delay
    if (iOS) {
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // For Android/Desktop, listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if we should show based on visit count
    const visits = parseInt(localStorage.getItem('visit-count') || '0', 10) + 1;
    localStorage.setItem('visit-count', visits.toString());
    
    // Show after 2nd visit if not iOS
    if (visits >= 2 && !iOS) {
      setTimeout(() => setShowBanner(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) {
      // If no prompt, show manual instructions
      setShowIOSInstructions(true);
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowBanner(false);
      localStorage.setItem('pwa-banner-dismissed', 'installed');
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
          >
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] border border-[#D4AF37]/30 rounded-2xl p-4 shadow-2xl shadow-black/50">
              <button 
                onClick={handleDismiss}
                className="absolute top-3 right-3 text-gray-500 hover:text-white transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-start gap-4">
                <div className="p-3 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-xl flex-shrink-0">
                  <Smartphone className="w-6 h-6 text-black" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-lg mb-1">
                    Get NC Help Hub App
                  </h3>
                  <p className="text-gray-400 text-sm mb-3">
                    Install our app for quick access to bookings, faster checkout, and instant notifications.
                  </p>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleInstallClick}
                      className="bg-[#D4AF37] hover:bg-[#B8860B] text-black font-medium flex-1"
                      data-testid="pwa-install-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Install Free
                    </Button>
                    <Button 
                      onClick={handleDismiss}
                      variant="outline" 
                      className="border-gray-600 text-gray-400 hover:text-white"
                    >
                      Not now
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* iOS Instructions Modal */}
      <AnimatePresence>
        {showIOSInstructions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
            onClick={() => setShowIOSInstructions(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[#1a1a1a] border border-[#D4AF37]/30 rounded-2xl p-6 max-w-sm w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Add to Home Screen</h3>
                <p className="text-gray-400 text-sm">
                  {isIOS 
                    ? "Follow these steps to install NC Help Hub on your iPhone/iPad:"
                    : "Follow these steps to install NC Help Hub:"}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                {isIOS ? (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#D4AF37] font-bold text-sm">1</div>
                      <p className="text-gray-300 text-sm">Tap the <span className="text-white font-medium">Share</span> button <span className="text-[#D4AF37]">(square with arrow)</span> at the bottom of Safari</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#D4AF37] font-bold text-sm">2</div>
                      <p className="text-gray-300 text-sm">Scroll down and tap <span className="text-white font-medium">"Add to Home Screen"</span></p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#D4AF37] font-bold text-sm">3</div>
                      <p className="text-gray-300 text-sm">Tap <span className="text-white font-medium">"Add"</span> to install the app</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#D4AF37] font-bold text-sm">1</div>
                      <p className="text-gray-300 text-sm">Tap the <span className="text-white font-medium">menu icon</span> <span className="text-[#D4AF37]">(⋮)</span> in your browser</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#D4AF37] font-bold text-sm">2</div>
                      <p className="text-gray-300 text-sm">Tap <span className="text-white font-medium">"Install app"</span> or <span className="text-white font-medium">"Add to Home screen"</span></p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#D4AF37] font-bold text-sm">3</div>
                      <p className="text-gray-300 text-sm">Confirm by tapping <span className="text-white font-medium">"Install"</span></p>
                    </div>
                  </>
                )}
              </div>

              <Button 
                onClick={() => setShowIOSInstructions(false)}
                className="w-full bg-[#D4AF37] hover:bg-[#B8860B] text-black font-medium"
              >
                Got it!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PWAInstallBanner;
